<script lang="ts">
  import Button from '$components/Button.svelte';
  import { createPopover } from 'svelte-headlessui';
  import Transition from 'svelte-transition';
  import TablerCircleChevronDown from '~icons/tabler/circle-chevron-down';
  import TablerCircleChevronUp from '~icons/tabler/circle-chevron-up';

  import { page } from '$app/stores';
  import { LayoutPreferenceType, type LayoutPreference } from '$types/races';
  import { Bookmaker } from '$utils/bookmakers';
  import { createDisclosure } from 'svelte-headlessui';

  $: ({ layoutConfigStore } = $page.data);
  $: activeLayoutConfigName = layoutConfigStore?.activeLayoutConfigName;
  $: activeLayoutConfig = layoutConfigStore?.activeLayoutConfig;

  function formatPriceType(type: LayoutPreferenceType) {
    switch (type) {
      case 'FIXED_WIN':
        return 'Win';
      case 'FIXED_WIN_FLUCS':
        return 'Win Flucs';
      case 'FIXED_PLACE':
        return 'Place';
      case 'FIXED_PLACE_FLUCS':
        return 'Place Flucs';
      default:
        return type;
    }
  }

  $: {
    if ($popover.expanded) {
      // close all accordions when popover is closed
      betfairDisclosure.close();
      fixedWinDisclosure.close();
      fixedPlaceDisclosure.close();
      fixedWinFlucsDisclosure.close();
      fixedPlaceFlucsDisclosure.close();
      otherDisclosure.close();
    }
  }

  function addColumn(
    type: LayoutPreference['type'],
    bookmaker?: Bookmaker | string,
    level?: LayoutPreference['level'],
    side?: LayoutPreference['side'],
    size?: LayoutPreference['size']
  ) {
    if (!layoutConfigStore || !$activeLayoutConfig) {
      // console.error('no layout config');
      return;
    }
    let name: string | null = null;
    // find a name if needed
    if (type === 'TEXT') {
      let index = 1;
      while (
        $activeLayoutConfig.layoutPreferences.find(
          (p) => p.name === `Custom ${index}`
        )
      ) {
        index++;
      }
      name = `Custom ${index}`;
    } else if (type === 'SPACER') {
      let index = 1;
      while (
        $activeLayoutConfig.layoutPreferences.find(
          (p) => p.name === `Spacer ${index}`
        )
      ) {
        index++;
      }
      name = `Spacer ${index}`;
    }

    layoutConfigStore.updateLayoutPreferences($activeLayoutConfig.name, [
      ...$activeLayoutConfig.layoutPreferences,
      {
        bookmaker,
        type: type,
        locked: false,
        level,
        side,
        name,
        size
      }
    ]);
  }

  const betfairDisclosure = createDisclosure({
    label: 'Betfair',
    expanded: false
  });
  const fixedWinDisclosure = createDisclosure({
    label: 'Fixed Win',
    expanded: false
  });
  const fixedPlaceDisclosure = createDisclosure({
    label: 'Fixed Place',
    expanded: false
  });
  const fixedWinFlucsDisclosure = createDisclosure({
    label: 'Fixed Win Flucs',
    expanded: false
  });
  const fixedPlaceFlucsDisclosure = createDisclosure({
    label: 'Fixed Place Flucs',
    expanded: false
  });
  const otherDisclosure = createDisclosure({
    label: 'Other Columns',
    expanded: false
  });
  const popover = createPopover({ label: 'Add Column' });

  let remainingBetfairColumns: LayoutPreference[] = [];
  $: {
    if ($popover.expanded) {
      remainingBetfairColumns = [];

      // add flucs
      if (
        $activeLayoutConfig?.layoutPreferences?.find(
          (p) =>
            p.bookmaker === 'Betfair' &&
            p.type === 'FIXED_WIN_FLUCS' &&
            p.side === 'BACK'
        ) === undefined
      ) {
        remainingBetfairColumns.push({
          bookmaker: 'Betfair',
          type: 'FIXED_WIN_FLUCS',
          side: 'BACK'
        } as LayoutPreference);
      }
      if (
        $activeLayoutConfig?.layoutPreferences?.find(
          (p) =>
            p.bookmaker === 'Betfair' &&
            p.type === 'FIXED_WIN_FLUCS' &&
            p.side === 'LAY'
        ) === undefined
      ) {
        remainingBetfairColumns.push({
          bookmaker: 'Betfair',
          type: 'FIXED_WIN_FLUCS',
          side: 'LAY'
        } as LayoutPreference);
      }

      // place flucs
      if (
        $activeLayoutConfig?.layoutPreferences?.find(
          (p) =>
            p.bookmaker === 'Betfair' &&
            p.type === 'FIXED_PLACE_FLUCS' &&
            p.side === 'BACK'
        ) === undefined
      ) {
        remainingBetfairColumns.push({
          bookmaker: 'Betfair',
          type: 'FIXED_PLACE_FLUCS',
          side: 'BACK'
        } as LayoutPreference);
      }
      if (
        $activeLayoutConfig?.layoutPreferences?.find(
          (p) =>
            p.bookmaker === 'Betfair' &&
            p.type === 'FIXED_PLACE_FLUCS' &&
            p.side === 'LAY'
        ) === undefined
      ) {
        remainingBetfairColumns.push({
          bookmaker: 'Betfair',
          type: 'FIXED_PLACE_FLUCS',
          side: 'LAY'
        } as LayoutPreference);
      }

      ['ONE', 'TWO', 'WEIGHTED_AVERAGE', 'ZERO'].forEach((level) => {
        ['BACK', 'LAY'].forEach((side) => {
          if (!layoutConfigStore || !$activeLayoutConfig) {
            // console.error('no layout config');
            return;
          }
          if (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) =>
                p.bookmaker === 'Betfair' &&
                p.level === level &&
                p.side === side &&
                p.type === 'FIXED_WIN'
            ) === undefined
          ) {
            remainingBetfairColumns.push({
              level,
              side,
              bookmaker: 'Betfair',
              type: 'FIXED_WIN'
            } as LayoutPreference);
          }
          if (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) =>
                p.bookmaker === 'Betfair' &&
                p.level === level &&
                p.side === side &&
                p.type === 'FIXED_PLACE'
            ) === undefined
          ) {
            remainingBetfairColumns.push({
              level,
              side,
              bookmaker: 'Betfair',
              type: 'FIXED_PLACE'
            } as LayoutPreference);
          }
        });
      });
      remainingBetfairColumns.sort((a, b) => {
        // always push place to the bottom
        if (a.type === 'FIXED_PLACE' && b.type !== 'FIXED_PLACE') {
          return 1;
        }
        if (a.type !== 'FIXED_PLACE' && b.type === 'FIXED_PLACE') {
          return -1;
        }

        if (a.level === b.level) {
          return a.side === 'BACK' ? -1 : 1;
        }

        // sort by level, zero,one,two,weighted_average
        return a.level === 'ZERO'
          ? -1
          : a.level === 'ONE'
            ? b.level === 'ZERO'
              ? 1
              : -1
            : a.level === 'TWO'
              ? b.level === 'WEIGHTED_AVERAGE'
                ? -1
                : 1
              : 1;
      });
    }
  }

  $: remainingFixedWinBookmakers = $popover.expanded
    ? (Object.values(Bookmaker) as string[])
        .filter((bookmaker) => {
          if (!layoutConfigStore || !$activeLayoutConfig) {
            // console.error('no layout config');
            return;
          }
          if (bookmaker === 'Betfair') return false;
          return (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) => p.bookmaker === bookmaker && p.type === 'FIXED_WIN'
            ) === undefined
          );
        })
        .sort()
    : [];
  $: remainingFixedPlaceBookmakers = $popover.expanded
    ? (Object.values(Bookmaker) as string[])
        .filter((bookmaker) => {
          if (!layoutConfigStore || !$activeLayoutConfig) {
            // console.error('no layout config');
            return;
          }
          if (bookmaker === 'Betfair') return false;
          return (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) => p.bookmaker === bookmaker && p.type === 'FIXED_PLACE'
            ) === undefined
          );
        })
        .sort()
    : [];
  $: remainingFixedWinFlucsBookmakers = $popover.expanded
    ? (Object.values(Bookmaker) as string[])
        .filter((bookmaker) => {
          if (!layoutConfigStore || !$activeLayoutConfig) {
            // console.error('no layout config');
            return;
          }
          if (bookmaker === 'Betfair') return false;
          return (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) => p.bookmaker === bookmaker && p.type === 'FIXED_WIN_FLUCS'
            ) === undefined
          );
        })
        .sort()
    : [];
  $: remainingFixedPlaceFlucsBookmakers = $popover.expanded
    ? (Object.values(Bookmaker) as string[])
        .filter((bookmaker) => {
          if (!layoutConfigStore || !$activeLayoutConfig) {
            // console.error('no layout config');
            return;
          }
          if (bookmaker === 'Betfair') return false;
          return (
            $activeLayoutConfig.layoutPreferences?.find(
              (p) => p.bookmaker === bookmaker && p.type === 'FIXED_PLACE_FLUCS'
            ) === undefined
          );
        })
        .sort()
    : [];

  const otherColumns = [
    { type: 'TEXT', size: 'SMALL', name: 'Text Input - Small' },
    { type: 'TEXT', size: 'MEDIUM', name: 'Text Input - Medium' },
    { type: 'TEXT', size: 'LARGE', name: 'Text Input - Large' },
    { type: 'SPACER', name: 'Spacer' }
  ] as LayoutPreference[];
</script>

<div use:popover.button>
  <Button
    class="{$popover.expanded
      ? ''
      : 'text-opacity-90'} group inline-flex items-center rounded-md bg-slate-900 px-3 py-2 text-base font-medium text-white hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
  >
    Add Column
    <TablerCircleChevronDown
      class="{$popover.expanded
        ? ''
        : 'text-opacity-70'} ml-2 h-5 w-5 text-white transition duration-150 ease-in-out group-hover:text-opacity-80"
    />
  </Button>
</div>

<Transition show={$popover.expanded}>
  <div
    use:popover.panel
    class="absolute right-0 top-10 z-50 mt-3 max-h-96 w-screen max-w-sm overflow-y-scroll rounded-lg border border-gray-100 bg-white px-4 shadow-xl sm:px-0"
  >
    <div class="mx-auto w-full rounded-2xl p-2">
      <div>
        <button
          use:betfairDisclosure.button
          class="flex w-full justify-between rounded-lg bg-orange-100 px-4 py-2 text-left text-sm font-medium text-orange-900 hover:bg-orange-200 focus:outline-none focus-visible:ring focus-visible:ring-orange-500 focus-visible:ring-opacity-75"
        >
          <span>Betfair</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-orange-500 {$betfairDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $betfairDisclosure.expanded}
          <div
            use:betfairDisclosure.panel
            class="flex flex-col gap-x-2 p-2 text-sm text-gray-500"
          >
            {#each remainingBetfairColumns as betfairPreference}
              <button
                on:click={() => {
                  addColumn(
                    betfairPreference.type,
                    'Betfair',
                    betfairPreference.level,
                    betfairPreference.side
                  );
                }}
                on:keypress={() => {
                  addColumn(
                    betfairPreference.type,
                    'Betfair',
                    betfairPreference.level,
                    betfairPreference.side
                  );
                }}
                class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div
                    class="bookmaker-betfair mr-3 h-5 w-5 rounded-full border border-black"
                  />
                  {#if betfairPreference.type === 'FIXED_WIN_FLUCS' || betfairPreference.type === 'FIXED_PLACE_FLUCS'}
                    Betfair {betfairPreference.side === 'BACK' ? 'Back' : 'Lay'}
                    Flucs
                  {:else}
                    Betfair - {betfairPreference.side === 'LAY'
                      ? 'Lay'
                      : 'Back'} -
                    {betfairPreference.level === 'ZERO'
                      ? '1st Level'
                      : betfairPreference.level === 'ONE'
                        ? '2nd Level'
                        : betfairPreference.level === 'TWO'
                          ? '3rd Level'
                          : 'Weighted Average'}
                  {/if}
                </a>
                <!-- pill at end -->
                <span
                  class="mr-2 text-xs font-medium text-gray-500"
                  class:font-bold={betfairPreference.type === 'FIXED_WIN' ||
                    betfairPreference.type === 'FIXED_WIN_FLUCS'}
                >
                  {formatPriceType(betfairPreference.type)}
                </span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="mt-2">
        <button
          use:fixedWinDisclosure.button
          class="flex w-full justify-between rounded-lg bg-green-100 px-4 py-2 text-left text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring focus-visible:ring-green-500 focus-visible:ring-opacity-75"
        >
          <span>Fixed Win</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-green-500 {$fixedWinDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $fixedWinDisclosure.expanded}
          <div
            use:fixedWinDisclosure.panel
            class="flex flex-col gap-x-2 p-2 text-sm text-gray-500"
          >
            <button
              on:click={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_WIN);
              }}
              on:keypress={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_WIN);
              }}
              class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <a
                class="group flex flex-1 items-center p-2 text-sm"
                role="menuitem"
                tabindex="-1"
                id="menu-item-0"
              >
                <div
                  class="bookmaker-any-bookmaker mr-3 h-5 w-5 rounded-full border border-black"
                />
                Best Price
              </a>
              <!-- pill at end -->
              <span class="mr-2 text-xs font-medium text-gray-500">
                Fixed Win
              </span>
            </button>

            {#each remainingFixedWinBookmakers as bookmaker}
              <button
                on:click={() => {
                  addColumn(LayoutPreferenceType.FIXED_WIN, bookmaker);
                }}
                on:keypress={() => {
                  addColumn(LayoutPreferenceType.FIXED_WIN, bookmaker);
                }}
                class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div
                    class="mr-3 h-5 w-5 rounded-full border border-black bookmaker-{bookmaker
                      .toLowerCase()
                      .replace(' ', '')}"
                  />
                  {bookmaker}
                </a>
                <!-- pill at end -->
                <span class="mr-2 flex text-xs font-medium text-gray-500">
                  Fixed Win
                </span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="mt-2">
        <button
          use:fixedWinFlucsDisclosure.button
          class="flex w-full justify-between rounded-lg bg-lime-100 px-4 py-2 text-left text-sm font-medium text-lime-900 hover:bg-lime-200 focus:outline-none focus-visible:ring focus-visible:ring-lime-500 focus-visible:ring-opacity-75"
        >
          <span>Fixed Win Flucs</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-lime-500 {$fixedWinFlucsDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $fixedWinFlucsDisclosure.expanded}
          <div
            use:fixedWinFlucsDisclosure.panel
            class="flex flex-col gap-x-2 p-2 text-sm text-gray-500"
          >
            {#each remainingFixedWinFlucsBookmakers as bookmaker}
              <button
                on:click={() => {
                  addColumn(LayoutPreferenceType.FIXED_WIN_FLUCS, bookmaker);
                }}
                on:keypress={() => {
                  addColumn(LayoutPreferenceType.FIXED_WIN_FLUCS, bookmaker);
                }}
                class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div
                    class="mr-3 h-5 w-5 rounded-full border border-black bookmaker-{bookmaker
                      .toLowerCase()
                      .replace(' ', '')}"
                  />
                  {bookmaker}
                </a>
                <!-- pill at end -->
                <span class="mr-2 flex text-xs font-medium text-gray-500">
                  Fixed Win Flucs
                </span>
              </button>
            {/each}
          </div>
        {/if}
      </div>

      <div class="mt-2">
        <button
          use:fixedPlaceDisclosure.button
          class="flex w-full justify-between rounded-lg bg-indigo-100 px-4 py-2 text-left text-sm font-medium text-indigo-900 hover:bg-indigo-200 focus:outline-none focus-visible:ring focus-visible:ring-indigo-500 focus-visible:ring-opacity-75"
        >
          <span>Fixed Place</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-indigo-500 {$fixedPlaceDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $fixedPlaceDisclosure.expanded}
          <div
            use:fixedPlaceDisclosure.panel
            class="flex flex-col gap-x-2 p-2 text-sm text-gray-500"
          >
            <button
              on:click={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_PLACE);
              }}
              on:keypress={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_PLACE);
              }}
              class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <a
                class="group flex flex-1 items-center p-2 text-sm"
                role="menuitem"
                tabindex="-1"
                id="menu-item-0"
              >
                <div
                  class="bookmaker-any-bookmaker mr-3 h-5 w-5 rounded-full border border-black"
                />
                Best Price
              </a>
              <!-- pill at end -->
              <span
                class="mr-2 flex items-center justify-center rounded-full text-xs font-medium text-gray-500"
              >
                Fixed Place
              </span>
            </button>
            {#each remainingFixedPlaceBookmakers as bookmaker}
              <button
                on:click={() => {
                  addColumn(LayoutPreferenceType.FIXED_PLACE, bookmaker);
                }}
                on:keypress={() => {
                  addColumn(LayoutPreferenceType.FIXED_PLACE, bookmaker);
                }}
                class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div
                    class="mr-3 h-5 w-5 rounded-full border border-black bookmaker-{bookmaker
                      .toLowerCase()
                      .replace(' ', '')}"
                  />
                  {bookmaker}
                </a>
                <!-- pill at end -->
                <span class="mr-2 flex text-xs font-medium text-gray-500">
                  Fixed Place
                </span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="mt-2">
        <button
          use:fixedPlaceFlucsDisclosure.button
          class="flex w-full justify-between rounded-lg bg-purple-100 px-4 py-2 text-left text-sm font-medium text-purple-900 hover:bg-purple-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75"
        >
          <span>Fixed Place Flucs</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-purple-500 {$fixedPlaceFlucsDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $fixedPlaceFlucsDisclosure.expanded}
          <div
            use:fixedPlaceFlucsDisclosure.panel
            class="flex flex-col gap-x-2 p-2 text-sm text-gray-500"
          >
            {#each remainingFixedPlaceFlucsBookmakers as bookmaker}
              <button
                on:click={() => {
                  addColumn(LayoutPreferenceType.FIXED_PLACE_FLUCS, bookmaker);
                }}
                on:keypress={() => {
                  addColumn(LayoutPreferenceType.FIXED_PLACE_FLUCS, bookmaker);
                }}
                class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div
                    class="mr-3 h-5 w-5 rounded-full border border-black bookmaker-{bookmaker
                      .toLowerCase()
                      .replace(' ', '')}"
                  />
                  {bookmaker}
                </a>
                <!-- pill at end -->
                <span class="mr-2 flex text-xs font-medium text-gray-500">
                  Fixed Place Flucs
                </span>
              </button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="mt-2">
        <button
          use:otherDisclosure.button
          class="flex w-full justify-between rounded-lg bg-sky-100 px-4 py-2 text-left text-sm font-medium text-sky-900 hover:bg-sky-200 focus:outline-none focus-visible:ring focus-visible:ring-sky-500 focus-visible:ring-opacity-75"
        >
          <span>Other Columns</span>
          <TablerCircleChevronUp
            class="h-5 w-5 text-sky-500 {$otherDisclosure.expanded
              ? 'rotate-180 transform'
              : ''}"
          />
        </button>
        {#if $otherDisclosure.expanded}
          <div
            use:otherDisclosure.panel
            class="flex flex-col px-4 pb-2 pt-4 text-sm text-gray-500"
          >
            <button
              on:click={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_WIN);
              }}
              on:keypress={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_WIN);
              }}
              class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <a
                class="group flex flex-1 items-center p-2 text-sm"
                role="menuitem"
                tabindex="-1"
                id="menu-item-0"
              >
                <div
                  class="bookmaker-any-bookmaker mr-3 h-5 w-5 rounded-full border border-black"
                />
                Best Price - Win
              </a>
              <!-- pill at end -->
              <span class="mr-2 flex text-xs font-medium text-gray-500">
                Fixed Win
              </span>
            </button>
            <button
              on:click={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_PLACE);
              }}
              on:keypress={() => {
                addColumn(LayoutPreferenceType.BEST_PRICE_PLACE);
              }}
              class="flex cursor-pointer flex-row place-items-center rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            >
              <a
                class="group flex flex-1 items-center p-2 text-sm"
                role="menuitem"
                tabindex="-1"
                id="menu-item-0"
              >
                <div
                  class="bookmaker-any-bookmaker mr-3 h-5 w-5 rounded-full border border-black"
                />
                Best Price - Place
              </a>
              <!-- pill at end -->
              <span
                class="mr-2 flex items-center justify-center rounded-full text-xs font-medium text-gray-500"
              >
                Fixed Place
              </span>
            </button>

            {#each otherColumns as other}
              <button
                on:click={() => {
                  addColumn(
                    other.type,
                    undefined,
                    undefined,
                    undefined,
                    other.size
                  );
                }}
                on:keypress={() => {
                  addColumn(
                    other.type,
                    undefined,
                    undefined,
                    undefined,
                    other.size
                  );
                }}
                class="flex cursor-pointer flex-row rounded-md text-gray-700 hover:bg-gray-100 hover:text-gray-900"
              >
                <!-- Active: "bg-gray-100 text-gray-900", Not Active: "text-gray-700" -->
                <a
                  class="group flex flex-1 items-center p-2 text-sm"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  <div class="mr-3 h-5 w-5 rounded-full border border-black" />
                  {other.name}
                </a>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div></Transition
>
